import React from 'react';
import { Link } from 'react-router-dom';
import { TabsContent } from '../ui/tabs';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../ui/alert-dialog';
import { Plus, FileSpreadsheet, FileText, Edit, Trash2, GitBranch } from 'lucide-react';
import { formatCurrency, formatDateShort } from '../../lib/utils';

export function TransactionsTab({
  project, transactions, jvAgreements, childLots = [],
  isOwner, isSuperOwner, isCompleted,
  onEditTransaction, onDeleteTransaction,
  onAddTransaction, onOpenQBOImport, onOpenSettlement,
}) {
  // Subdivision query-time rollup: child lot txns surface in the parent
  // ledger (read-only here, edited on the child's own page).
  const childMap = {};
  (childLots || []).forEach((c) => { childMap[c.id] = c.name; });
  const rollupCount = (transactions || []).filter(
    (t) => t.project_id && t.project_id !== project.id
  ).length;

  // Multi-investor deals: investors pooled under one JV agreement. Surface how
  // much each investor has funded (sum of partner_id-tagged funding txns).
  const coInvestors = (jvAgreements || []).flatMap((jv) =>
    (jv.co_investors || []).map((ci) => ({ ...ci, jv_id: jv.id }))
  );
  const coInvestorNameMap = Object.fromEntries(coInvestors.map((c) => [c.id, c.name]));
  const hasCoInvestors = coInvestors.length > 0;
  const FUNDING_TYPES = ['Purchase_Price', 'EMD', 'Expense', 'Capital_In', 'Investor_Contribution', 'Investor_Advance'];
  const fundedByPartner = {};
  if (hasCoInvestors) {
    (transactions || []).forEach((t) => {
      if (t.partner_id && FUNDING_TYPES.includes(t.type)) {
        fundedByPartner[t.partner_id] = (fundedByPartner[t.partner_id] || 0) + (t.amount || 0);
      }
    });
  }
  return (
    <TabsContent value="transactions">
      <Card className="rounded-sm border">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-sm font-semibold uppercase tracking-wide">Transactions</CardTitle>
            <div className="flex gap-2 flex-wrap">
              {isOwner && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={onOpenQBOImport}
                    className="rounded-sm text-xs border-purple-300 text-purple-700 hover:bg-purple-50"
                    data-testid="import-qbo-btn"
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-1" /> Import from QBO
                  </Button>
                  {/* HUD/Settlement Statement entry — captures the whole
                      closing in one structured pass and writes the atomic
                      ledger set. The deed price becomes project.purchase_price;
                      EMD + closing costs become individual categorized txns. */}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onOpenSettlement?.('buy')}
                    className="rounded-sm text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    data-testid="settlement-buy-btn"
                  >
                    <FileText className="w-4 h-4 mr-1" /> Purchase HUD
                  </Button>
                  {(project?.status === 'Owned' || project?.status === 'Sold' || project?.status === 'Note_Held') && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onOpenSettlement?.('sell')}
                      className="rounded-sm text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                      data-testid="settlement-sell-btn"
                    >
                      <FileText className="w-4 h-4 mr-1" /> Sale HUD
                    </Button>
                  )}
                  {/* Wave-4 follow-up: removed dedicated "Record Purchase" /
                      "Record EMD" buttons. Both are now recorded through the
                      single "Add Transaction" entry — pick type=Purchase Price
                      or type=EMD from the type dropdown. Consolidates the data-
                      entry paths so calculations have a single source of truth
                      and there are fewer ways to introduce drift. */}
                  <Button
                    size="sm"
                    onClick={() => onAddTransaction(null)}
                    className="bg-forest-500 hover:bg-forest-600 text-white rounded-sm text-xs"
                    data-testid="add-transaction-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add Transaction
                  </Button>
                </>
              )}
            </div>
          </div>
          {project.parent_project_id && (
            <p className="text-xs text-amber-600 mt-2 bg-amber-50 p-2 rounded-sm">
              Purchase Price and EMD transactions should be recorded on the parent project.
            </p>
          )}
          {rollupCount > 0 && (
            <p className="text-xs text-purple-700 mt-2 bg-purple-50 p-2 rounded-sm flex items-center gap-1.5" data-testid="rollup-note">
              <GitBranch className="w-3.5 h-3.5" />
              Includes {rollupCount} transaction{rollupCount !== 1 ? 's' : ''} rolled up from {childLots.length} child lot{childLots.length !== 1 ? 's' : ''}. Lot rows are read-only here — edit them on the lot&rsquo;s own page.
            </p>
          )}
        </CardHeader>
        <CardContent>
          {hasCoInvestors && (
            <div className="mb-4 rounded-sm border border-blue-200 bg-blue-50/40 p-3" data-testid="funding-by-investor-summary">
              <p className="text-[11px] uppercase tracking-wide text-blue-800 font-semibold mb-2">Funding by Investor</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {coInvestors.map((ci) => {
                  const funded = fundedByPartner[ci.id] || 0;
                  const committed = ci.capital_committed || 0;
                  return (
                    <div key={ci.id} className="rounded-sm bg-white border border-blue-100 p-2" data-testid={`funding-investor-${ci.id}`}>
                      <p className="text-sm font-medium text-slate-800">{ci.name}</p>
                      <div className="flex items-center justify-between text-xs mt-1">
                        <span className="text-muted-foreground">Funded</span>
                        <span className="font-mono font-semibold text-emerald-700" data-testid={`funding-investor-amount-${ci.id}`}>{formatCurrency(funded)}</span>
                      </div>
                      {committed > 0 && (
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">Committed</span>
                          <span className="font-mono text-slate-600">{formatCurrency(committed)}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <p className="text-[10px] text-muted-foreground mt-2">
                Sum of purchase, EMD, expense and capital-in transactions tagged to each investor.
              </p>
            </div>
          )}
          {transactions.length > 0 ? (
            <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0" style={{ WebkitOverflowScrolling: 'touch' }}>
              <table className="data-table min-w-[600px]">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Category</th>
                    <th>Paid/Received By</th>
                    <th>Description</th>
                    <th>Amount</th>
                    {isOwner && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((t) => {
                    let paidBy = 'Shared';
                    if (t.responsible_party === 'Land_Finder') {
                      paidBy = 'Land Finder';
                    } else if (t.responsible_party === 'Investor' || t.jv_agreement_id) {
                      const jv = jvAgreements.find(j => j.id === t.jv_agreement_id);
                      paidBy = jv ? jv.investor_name : 'Investor';
                    }
                    
                    const isReimbursement = ['Reimbursement', 'Refund', 'Vendor_Reimbursement'].includes(t.type);
                    let receivedBy = null;
                    if (isReimbursement && t.received_by) {
                      receivedBy = t.received_by === 'Land_Finder' ? 'Land Finder' : 'Investor';
                    }

                    const lotName = t.project_id && t.project_id !== project.id
                      ? (childMap[t.project_id] || 'Lot')
                      : null;
                    const isRollup = Boolean(lotName);

                    return (
                      <tr key={t.id} className={isRollup ? 'bg-purple-50/40' : (t.qbo_synced && !t.qbo_confirmed ? 'bg-blue-50/40' : '')} data-testid={isRollup ? `rollup-txn-${t.id}` : undefined}>
                        <td className="font-mono text-xs">
                          {formatDateShort(t.date)}
                          {lotName && (
                            <Link to={`/projects/${t.project_id}`}>
                              <Badge variant="outline" className="ml-1 text-[9px] rounded-sm bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100">
                                Lot: {lotName}
                              </Badge>
                            </Link>
                          )}
                          {t.qbo_synced && (
                            <Badge variant="outline" className={`ml-1 text-[9px] rounded-sm ${t.qbo_confirmed ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-blue-50 text-blue-600 border-blue-200'}`}>
                              {t.qbo_confirmed ? 'QBO' : 'QBO Review'}
                            </Badge>
                          )}
                        </td>
                        <td>
                          <Badge 
                            variant="outline" 
                            className={`rounded-sm text-xs ${
                              t.type === 'Expense' ? 'bg-red-50 text-red-700 border-red-200' :
                              t.type === 'Capital_In' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                              t.type === 'Reimbursement' ? 'bg-blue-50 text-emerald-600 border-blue-200' :
                              t.type === 'Refund' ? 'bg-teal-50 text-teal-700 border-teal-200' :
                              'bg-gray-50 text-gray-700 border-gray-200'
                            }`}
                          >
                            {t.type.replace('_', ' ')}
                          </Badge>
                        </td>
                        <td className="text-xs">{t.category}</td>
                        <td>
                          {isReimbursement ? (
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              receivedBy === 'Land Finder' ? 'bg-forest-50 text-forest-700' :
                              receivedBy === 'Investor' ? 'bg-blue-50 text-emerald-600' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {receivedBy ? `\u2192 ${receivedBy}` : 'Received by: -'}
                            </span>
                          ) : (
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              paidBy === 'Land Finder' ? 'bg-forest-50 text-forest-700' :
                              paidBy === 'Shared' ? 'bg-gray-100 text-gray-600' :
                              'bg-blue-50 text-emerald-600'
                            }`}>
                              {paidBy}
                            </span>
                          )}
                          {t.partner_id && coInvestorNameMap[t.partner_id] && (
                            <span className="block mt-0.5 text-[9px] text-blue-700" data-testid={`txn-partner-${t.id}`}>
                              &#8627; {coInvestorNameMap[t.partner_id]}
                            </span>
                          )}
                        </td>
                        <td className="text-xs text-muted-foreground max-w-[150px] truncate">
                          {t.description || '-'}
                        </td>
                        <td className={`font-mono text-sm font-medium ${
                          ['Expense', 'Purchase_Price'].includes(t.type) ? 'text-red-600' :
                          ['EMD'].includes(t.type) ? 'text-blue-600' :
                          ['Sale_Revenue', 'Capital_In', 'Refund', 'Reimbursement', 'Pre_Sale_Payment'].includes(t.type) ? 'text-emerald-600' :
                          'text-foreground'
                        }`}>
                          {['Expense', 'Purchase_Price'].includes(t.type) ? '-' : ''}
                          {['Sale_Revenue', 'Capital_In', 'Refund', 'Reimbursement', 'Vendor_Reimbursement', 'Pre_Sale_Payment'].includes(t.type) ? '+' : ''}
                          {formatCurrency(t.amount)}
                        </td>
                        {isOwner && (
                        <td>
                          <div className="flex items-center justify-center gap-1">
                                {isRollup ? (
                                  <span className="text-[10px] text-purple-400 italic">on lot page</span>
                                ) : (
                                <>
                                {!isCompleted && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => onEditTransaction(t)}
                                  className="h-7 w-7 p-0"
                                >
                                  <Edit className="w-3 h-3" />
                                </Button>
                                )}
                                {isSuperOwner && !isCompleted && (
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600">
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent className="rounded-sm">
                                    <AlertDialogHeader>
                                      <AlertDialogTitle className="flex items-center gap-2 text-red-600">
                                        <Trash2 className="w-5 h-5" />
                                        Delete Transaction
                                      </AlertDialogTitle>
                                      <AlertDialogDescription className="space-y-3">
                                        <p>Are you sure you want to delete this transaction?</p>
                                        <div className="bg-red-50 border border-red-200 rounded-sm p-3 text-sm">
                                          <p className="font-medium text-red-800 mb-1">Warning: This action is permanent</p>
                                          <ul className="text-red-700 text-xs space-y-1">
                                            <li>&#8226; <strong>{t.category}</strong> - {formatCurrency(t.amount)}</li>
                                            <li>&#8226; This will affect project financial calculations</li>
                                            <li>&#8226; This data cannot be recovered after deletion</li>
                                          </ul>
                                        </div>
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                                    <AlertDialogAction 
                                      onClick={() => onDeleteTransaction(t.id)}
                                      className="bg-red-600 hover:bg-red-700 rounded-sm"
                                    >
                                      Yes, Delete Transaction
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                                )}
                                </>
                                )}
                        </div>
                      </td>
                        )}
                    </tr>
                  );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="py-8 text-center text-muted-foreground">
              {isOwner ? 'No transactions yet. Add your first transaction.' : 'No transactions recorded.'}
            </div>
          )}
        </CardContent>
      </Card>
    </TabsContent>
  );
}
